import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react"
import { useNavigate } from "react-router-dom"
import {
  BattleStartedPayload,
  ChallengeReceivedPayload,
  ChallengeSendDto,
  ChallengeSentPayload,
  ColorRank,
  OnlinePlayer,
  RadarSearchMode,
} from "@/types/battle"
import { USE_MOCK_BATTLE } from "@/constants/guardians"
import { useAuth } from "@/context/AuthContext"
import {
  connectBattleSocket,
  consumePendingBattleStarted,
  emitBattleEventWhenReady,
  onBattleEvent,
} from "@/socket/battleSocket"
import {
  normalizeChallengeReceivedPayload,
  normalizeChallengeSentPayload,
  normalizeChallengeStatusPayload,
} from "@/utils/challengePayload"
import IncomingChallengeModal from "@/features/battle/IncomingChallengeModal"

const ACCEPT_RESPONSE_TIMEOUT_MS = 12_000
const PRESENCE_ERROR_MSG =
  "Арена ещё подключается. Подождите пару секунд и попробуйте снова."

type BattleChallengeContextValue = {
  incomingChallenge: ChallengeReceivedPayload | null
  outgoingChallenge: ChallengeSentPayload | null
  acceptingChallenge: boolean
  sendChallenge: (
    target: OnlinePlayer,
    searchMode: RadarSearchMode,
    colorRank?: ColorRank
  ) => void
  acceptIncoming: () => void
  declineIncoming: () => void
  cancelOutgoing: () => void
  clearOutgoing: () => void
}

const BattleChallengeContext = createContext<BattleChallengeContextValue>({
  incomingChallenge: null,
  outgoingChallenge: null,
  acceptingChallenge: false,
  sendChallenge: () => {},
  acceptIncoming: () => {},
  declineIncoming: () => {},
  cancelOutgoing: () => {},
  clearOutgoing: () => {},
})

export function useBattleChallenge() {
  return useContext(BattleChallengeContext)
}

export function BattleChallengeProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate()
  const { token, isAuthenticated } = useAuth()
  const [incomingChallenge, setIncomingChallenge] =
    useState<ChallengeReceivedPayload | null>(null)
  const [outgoingChallenge, setOutgoingChallenge] =
    useState<ChallengeSentPayload | null>(null)
  const [acceptingChallenge, setAcceptingChallenge] = useState(false)

  const navigatingRef = useRef(false)
  const pendingAcceptIdRef = useRef<string | null>(null)
  const acceptTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const awaitingChallengeBattleRef = useRef(false)
  const sendingChallengeRef = useRef(false)

  const clearAcceptTimeout = useCallback(() => {
    if (acceptTimeoutRef.current) {
      clearTimeout(acceptTimeoutRef.current)
      acceptTimeoutRef.current = null
    }
  }, [])

  const finishAcceptPending = useCallback(() => {
    pendingAcceptIdRef.current = null
    awaitingChallengeBattleRef.current = false
    setAcceptingChallenge(false)
    clearAcceptTimeout()
  }, [clearAcceptTimeout])

  const clearOutgoingAwaiting = useCallback(() => {
    awaitingChallengeBattleRef.current = false
    sendingChallengeRef.current = false
  }, [])

  const goDuel = useCallback(
    (battle: BattleStartedPayload) => {
      if (navigatingRef.current) return
      navigatingRef.current = true
      finishAcceptPending()
      clearOutgoingAwaiting()
      setIncomingChallenge(null)
      setOutgoingChallenge(null)
      navigate("/play/duel", { state: { battle } })
      setTimeout(() => {
        navigatingRef.current = false
      }, 500)
    },
    [navigate, finishAcceptPending, clearOutgoingAwaiting]
  )

  const handleBattleError = useCallback(
    (message: string) => {
      const inChallengeFlow =
        pendingAcceptIdRef.current != null ||
        awaitingChallengeBattleRef.current ||
        sendingChallengeRef.current

      if (!inChallengeFlow) return

      if (pendingAcceptIdRef.current != null) {
        finishAcceptPending()
      } else {
        clearOutgoingAwaiting()
      }

      window.alert(message || "Не удалось выполнить действие")
    },
    [finishAcceptPending, clearOutgoingAwaiting]
  )

  const clearOutgoing = useCallback(() => {
    clearOutgoingAwaiting()
    setOutgoingChallenge(null)
  }, [clearOutgoingAwaiting])

  const sendChallenge = useCallback(
    (target: OnlinePlayer, searchMode: RadarSearchMode, colorRank?: ColorRank) => {
      if (USE_MOCK_BATTLE) return
      if (sendingChallengeRef.current || awaitingChallengeBattleRef.current) return

      const payload: ChallengeSendDto = {
        targetUserId: target.userId,
        searchMode,
      }
      if (searchMode === "RANK" && colorRank) {
        payload.colorRank = colorRank
      }

      sendingChallengeRef.current = true

      void (async () => {
        const sent = await emitBattleEventWhenReady(token, "challenge-send", payload)
        if (!sent) {
          sendingChallengeRef.current = false
          window.alert(PRESENCE_ERROR_MSG)
        }
      })()
    },
    [token]
  )

  const acceptIncoming = useCallback(() => {
    if (!incomingChallenge || acceptingChallenge) return

    const challengeId = incomingChallenge.challengeId
    if (!challengeId?.trim()) {
      window.alert("Некорректный вызов — попробуйте снова")
      return
    }

    pendingAcceptIdRef.current = challengeId
    awaitingChallengeBattleRef.current = true
    setAcceptingChallenge(true)

    clearAcceptTimeout()
    acceptTimeoutRef.current = setTimeout(() => {
      if (pendingAcceptIdRef.current !== challengeId) return
      finishAcceptPending()
      window.alert("Ответ сервера не получен. Проверьте соединение.")
    }, ACCEPT_RESPONSE_TIMEOUT_MS)

    void (async () => {
      const sent = await emitBattleEventWhenReady(token, "challenge-accept", {
        challengeId,
      })
      if (!sent) {
        finishAcceptPending()
        window.alert(PRESENCE_ERROR_MSG)
      }
    })()
  }, [
    incomingChallenge,
    acceptingChallenge,
    token,
    clearAcceptTimeout,
    finishAcceptPending,
  ])

  const declineIncoming = useCallback(() => {
    if (!incomingChallenge) return
    finishAcceptPending()
    void emitBattleEventWhenReady(token, "challenge-decline", {
      challengeId: incomingChallenge.challengeId,
    })
    setIncomingChallenge(null)
  }, [incomingChallenge, token, finishAcceptPending])

  const cancelOutgoing = useCallback(() => {
    if (!outgoingChallenge) return
    void emitBattleEventWhenReady(token, "challenge-cancel", {
      challengeId: outgoingChallenge.challengeId,
    })
    clearOutgoing()
  }, [outgoingChallenge, token, clearOutgoing])

  useEffect(() => {
    if (USE_MOCK_BATTLE || !isAuthenticated || !token) return

    const cleanups = [
      onBattleEvent("error", (payload) => {
        handleBattleError(payload.message)
      }),
      onBattleEvent("connect_error", (payload) => {
        handleBattleError(payload.message)
      }),
      onBattleEvent("challenge-received", (payload) => {
        pendingAcceptIdRef.current = null
        setAcceptingChallenge(false)
        clearAcceptTimeout()
        setOutgoingChallenge(null)
        clearOutgoingAwaiting()

        const normalized = normalizeChallengeReceivedPayload(payload)
        if (!normalized) {
          window.alert("Не удалось обработать входящий вызов.")
          return
        }
        setIncomingChallenge(normalized)
      }),
      onBattleEvent("challenge-sent", (payload) => {
        finishAcceptPending()
        setIncomingChallenge(null)
        sendingChallengeRef.current = false

        const normalized = normalizeChallengeSentPayload(payload)
        if (!normalized) {
          clearOutgoingAwaiting()
          window.alert("Сервер подтвердил вызов, но ответ не распознан.")
          return
        }

        setOutgoingChallenge(normalized)
        awaitingChallengeBattleRef.current = true
        navigate("/play/challenge-waiting")
      }),
      onBattleEvent("challenge-declined", (payload) => {
        const status = normalizeChallengeStatusPayload(payload)
        if (!status) return
        setOutgoingChallenge((prev) => {
          if (prev?.challengeId !== status.challengeId) return prev
          clearOutgoingAwaiting()
          window.alert(status.message || "Соперник отклонил вызов")
          return null
        })
      }),
      onBattleEvent("challenge-cancelled", (payload) => {
        const status = normalizeChallengeStatusPayload(payload)
        if (!status) return
        setOutgoingChallenge((prev) => {
          if (prev?.challengeId !== status.challengeId) return prev
          clearOutgoingAwaiting()
          return null
        })
        setIncomingChallenge((prev) => {
          if (prev?.challengeId !== status.challengeId) return prev
          return null
        })
      }),
      onBattleEvent("challenge-expired", (payload) => {
        const status = normalizeChallengeStatusPayload(payload)
        if (!status) return
        setOutgoingChallenge((prev) => {
          if (prev?.challengeId !== status.challengeId) return prev
          clearOutgoingAwaiting()
          window.alert(status.message || "Время вызова истекло")
          return null
        })
        setIncomingChallenge((prev) => {
          if (prev?.challengeId !== status.challengeId) return prev
          window.alert(status.message || "Время вызова истекло")
          return null
        })
      }),
      onBattleEvent("battle-started", (payload) => {
        const inChallengeFlow =
          awaitingChallengeBattleRef.current ||
          pendingAcceptIdRef.current != null

        if (!inChallengeFlow) return
        goDuel(payload)
      }),
    ]

    void connectBattleSocket(token)
      .then(() => {
        const pending = consumePendingBattleStarted()
        const inChallengeFlow =
          awaitingChallengeBattleRef.current ||
          pendingAcceptIdRef.current != null
        if (pending && inChallengeFlow) {
          goDuel(pending)
        }
      })
      .catch(() => {})

    return () => {
      cleanups.forEach((fn) => fn())
      clearAcceptTimeout()
    }
  }, [
    token,
    isAuthenticated,
    goDuel,
    handleBattleError,
    finishAcceptPending,
    clearAcceptTimeout,
    clearOutgoingAwaiting,
    navigate,
  ])

  const value = useMemo(
    () => ({
      incomingChallenge,
      outgoingChallenge,
      acceptingChallenge,
      sendChallenge,
      acceptIncoming,
      declineIncoming,
      cancelOutgoing,
      clearOutgoing,
    }),
    [
      incomingChallenge,
      outgoingChallenge,
      acceptingChallenge,
      sendChallenge,
      acceptIncoming,
      declineIncoming,
      cancelOutgoing,
      clearOutgoing,
    ]
  )

  return (
    <BattleChallengeContext.Provider value={value}>
      {children}
      <IncomingChallengeModal
        challenge={incomingChallenge}
        accepting={acceptingChallenge}
        onAccept={acceptIncoming}
        onDecline={declineIncoming}
      />
    </BattleChallengeContext.Provider>
  )
}
